import { useState, useEffect, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { useApiConfig } from "common-fe";
import { useTranslation } from "i18n-fe";
import { OrganizationApi, Configuration } from "account-common";
import { extractErrorMessage } from "../utils/errorUtils";

const ACCOUNT_API_BASE_URL = import.meta.env.VITE_ACCOUNT_API_BASE_URL;

export const useOrganizations = () => {
  const auth = useAuth();
  const buildConfig = useApiConfig();
  const { t } = useTranslation("common");
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set up the API client
  const api = useCallback(() => {
    const accessToken = auth.user?.access_token;
    if (!accessToken) return null;
    const config = new Configuration(buildConfig(ACCOUNT_API_BASE_URL));
    return new OrganizationApi(config);
  }, [auth.user?.access_token, buildConfig]);

  // Fetch organizations
  const fetchOrganizations = useCallback(async () => {
    const organizationApi = api();
    if (!organizationApi) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await organizationApi.listOrganizations();
      
      if (response.data.status === "success" && response.data.data) {
        setOrganizations(response.data.data);
      } else {
        setError("Failed to load organizations");
      }
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
      setError(extractErrorMessage(err, t));
    } finally {
      setLoading(false);
    }
  }, [api, t]);

  // Create organization
  const createOrganization = useCallback(async (organizationData) => {
    const organizationApi = api();
    if (!organizationApi) {
      throw new Error("No access token found");
    }

    try {
      const response = await organizationApi.createOrganization(organizationData);
      
      if (response.data.status === "success" && response.data.data) {
        // Add the new organization to the list
        const newOrg = response.data.data.organization;
        setOrganizations(prev => [...prev, { 
          id: newOrg.id, 
          name: newOrg.name,
          owner: true, // Creator is always the owner
          active: newOrg.active !== undefined ? newOrg.active : true
        }]);
        return response.data.data;
      } else {
        throw new Error("Failed to create organization");
      }
    } catch (err) {
      console.error("Failed to create organization:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, t]);

  // Update organization
  const updateOrganization = useCallback(async (id, organizationData) => {
    const organizationApi = api();
    if (!organizationApi) {
      throw new Error("No access token found");
    }

    try {
      const response = await organizationApi.updateOrganization(id, organizationData);
      
      if (response.data.status === "success" && response.data.data) {
        // Update the organization in the list
        const updatedOrg = response.data.data.organization;
        setOrganizations(prev => 
          prev.map(org => 
            org.id === id 
              ? { ...org, name: updatedOrg.name, active: updatedOrg.active, owner: updatedOrg.owner }
              : org
          )
        );
        return response.data.data;
      } else {
        throw new Error("Failed to update organization");
      }
    } catch (err) {
      console.error("Failed to update organization:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, t]);

  // Get single organization details
  const getOrganization = useCallback(async (id) => {
    const organizationApi = api();
    if (!organizationApi) {
      throw new Error("No access token found");
    }

    try {
      const response = await organizationApi.getOrganization(id);
      
      if (response.data.status === "success" && response.data.data) {
        return response.data.data.organization;
      } else {
        throw new Error("Failed to get organization details");
      }
    } catch (err) {
      console.error("Failed to get organization:", err);
      throw new Error(extractErrorMessage(err, t));
    }
  }, [api, t]);

  // Fetch organizations on mount and when auth changes
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  return {
    organizations,
    loading,
    error,
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    getOrganization,
    refetch: fetchOrganizations
  };
}; 